/**
 * Calendar Page Component
 * Full-page calendar view for case managers
 */

import React, { useState } from 'react';
import { Calendar as CalendarIcon, Settings, Users, Clock } from 'lucide-react';
import Calendar from './Calendar';
import AvailabilityManager from './AvailabilityManager';
import { cn } from '@/lib/utils';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';

type TabType = 'calendar' | 'availability';

interface CalendarPageProps {
  /** App-shell tab switcher. Threaded down to Calendar so Hub Meetings /
   *  Trainings open the full-screen EventDetailPage in Activity Tracking
   *  instead of the appointment-shaped modal. */
  onNavigate?: (tab: string) => void;
}

export default function CalendarPage({ onNavigate }: CalendarPageProps = {}) {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [activeTab, setActiveTab] = useState<TabType>('calendar');

  return (
    <div className="h-full flex flex-col">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 border-b px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
          <div className="min-w-0 flex-shrink">
            {!tabbed && (
              <h1 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100 sm:text-2xl">
                <CalendarIcon className="h-5 w-5 flex-shrink-0 text-blue-600 sm:h-6 sm:w-6" />
                Calendar
              </h1>
            )}
            <p className="mt-1 hidden text-sm text-gray-500 dark:text-gray-400 sm:block">
              Manage your schedule and appointments
            </p>
          </div>

          {/* Tab Switcher */}
          <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-700">
            <button
              onClick={() => setActiveTab('calendar')}
              className={cn(
                'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors sm:gap-2 sm:px-4 sm:py-2 sm:text-sm',
                activeTab === 'calendar'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              )}
            >
              <CalendarIcon className="h-4 w-4" />
              Calendar
            </button>
            <button
              onClick={() => setActiveTab('availability')}
              className={cn(
                'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors sm:gap-2 sm:px-4 sm:py-2 sm:text-sm',
                activeTab === 'availability'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              )}
            >
              <Clock className="h-4 w-4" />
              Availability
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden p-3 sm:p-6">
        {activeTab === 'calendar' ? (
          <Calendar className="h-full" defaultView="week" onNavigate={onNavigate} />
        ) : (
          <AvailabilityManager />
        )}
      </div>
    </div>
  );
}
